// Faixa permanente do acesso emergencial (break-glass) em vigor (Parte 5.2 "destaque em toda tela"; Parte 3.6 `contexts[].activeBreakGlass[]`;
// REQ-AUTHZ-016; OPEN-078; GIY-073 parte B): aparece em toda tela enquanto o contexto ativo (o tenant da rota) tem um acesso emergencial aberto pelo
// usuário — o estudo, o instante em que expira e o prazo da revisão obrigatória. Nada além disso vem de `GET /me`: a razão declarada nunca chega ao shell.
// O acesso emergencial vence sozinho: a faixa só lista as concessões cujo `expiresAt` ainda não passou e reavalia no próximo vencimento, sem esperar um novo `/me`.
import type { MeContext } from '@giyus/api-client';
import { Alert, AlertDescription, AlertTitle } from '@giyus/ui';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { useMe } from '../lib/auth/authContext';
import { useContextStore } from '../lib/context/contextStore';
import { useDateFormatter } from '../lib/dates/useDateFormatter';

type MeActiveBreakGlass = MeContext['activeBreakGlass'][number];

/** O maior atraso que `setTimeout` aceita (2^31 − 1 ms ≈ 24,8 dias); acima disso ele dispara na hora. */
const MAX_TIMER_MS = 2_147_483_647;

/** Ainda em vigor? Um `expiresAt` ilegível não esconde a faixa (transparência: na dúvida, mostra). */
function isInForce(grant: Pick<MeActiveBreakGlass, 'expiresAt'>, now: number): boolean {
  const expires = Date.parse(grant.expiresAt);
  return Number.isNaN(expires) || expires > now;
}

/**
 * As concessões ainda em vigor. O relógio é estado: um timer o atualiza no próximo `expiresAt` (a faixa some sozinha na expiração) e também logo após um `/me` novo
 * trazer um vencimento que o último instante lido não conhecia (o timer dispara em ~1 ms e o filtro passa a usar o instante real).
 */
function useGrantsInForce(grants: readonly MeActiveBreakGlass[]): readonly MeActiveBreakGlass[] {
  const [now, setNow] = useState(() => Date.now());
  const inForce = grants.filter((grant) => isInForce(grant, now));
  const deadlines = inForce.map((grant) => Date.parse(grant.expiresAt)).filter(Number.isFinite);
  const nextExpiry = deadlines.length === 0 ? null : Math.min(...deadlines);

  useEffect(() => {
    if (nextExpiry === null) {
      return undefined;
    }
    const delay = Math.min(Math.max(nextExpiry - Date.now(), 0) + 1, MAX_TIMER_MS);
    const timer = setTimeout(() => {
      setNow(Date.now());
    }, delay);
    return () => {
      clearTimeout(timer);
    };
  }, [nextExpiry, now]);

  return inForce;
}

export function BreakGlassBanner() {
  const { t } = useTranslation('shell');
  const dates = useDateFormatter();
  const me = useMe();
  const activeTenantCode = useContextStore((s) => s.activeTenantCode);
  const active = useGrantsInForce(
    me.contexts.find((context) => context.code === activeTenantCode)?.activeBreakGlass ?? [],
  );
  if (active.length === 0) {
    return null;
  }

  return (
    <div className="px-4 pt-3 md:px-8" data-testid="break-glass-banner">
      <Alert variant="warning" role="region" aria-label={t('breakGlassBanner.label')}>
        <AlertTitle>{t('breakGlassBanner.title', { count: active.length })}</AlertTitle>
        <AlertDescription>
          <ul className="flex flex-col gap-1">
            {active.map((grant) => (
              <li
                key={`${grant.scopeType}-${grant.scopeId}`}
                data-testid={`break-glass-banner-item-${grant.scopeId}`}
              >
                {t('breakGlassBanner.description', {
                  scope: grant.scopeId,
                  expires: dates.dateTime(grant.expiresAt),
                  review: dates.dateTime(grant.reviewDueAt),
                })}
              </li>
            ))}
          </ul>
        </AlertDescription>
      </Alert>
    </div>
  );
}
